import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhMalwareApi } from 'src/api/mindtime/wazuhMalwareApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhMalwareCategoriesData,
  WazuhMalwareDetectionItem,
  WazuhMalwareDetectionsQuery,
  WazuhMalwareSummaryData,
} from 'src/types/mindtime/wazuhMalware';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type MalwareFacets = {
  types: string[];
};

export type MalwareFilters = {
  severityBand?: string;
  malwareType?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type MalwareState = {
  loading: boolean;
  summary: WazuhMalwareSummaryData | null;
  items: WazuhPagedData<WazuhMalwareDetectionItem> | null;
  categories: WazuhMalwareCategoriesData | null;
  facets: MalwareFacets;
};

type BuildMalwareQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: MalwareFilters;
  page?: number;
  pageSize?: number;
};

const emptyFacets = (): MalwareFacets => ({
  types: [],
});

const emptyState = (): MalwareState => ({
  loading: true,
  summary: null,
  items: null,
  categories: null,
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhMalwareDetectionItem[],
  previous: MalwareFacets,
  categoryKeys: string[] = []
): MalwareFacets => {
  const types = new Set(previous.types);
  categoryKeys.forEach((key) => {
    if (key) types.add(key);
  });
  items.forEach((item) => {
    if (item.malwareType) types.add(item.malwareType);
  });
  return {
    types: Array.from(types).sort((a, b) => a.localeCompare(b)),
  };
};

const buildMalwareQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  page,
  pageSize,
}: BuildMalwareQueryInput): WazuhMalwareDetectionsQuery => {
  const { severityBand, malwareType, wazuhAgentId, organizationId } = filters;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(malwareType ? { malwareType } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useMalwareExport = (search = '', filters: MalwareFilters = {}) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    mindtimeWazuhMalwareApi
      .export(buildMalwareQuery({ scope, profileOrgId, isReseller, search, filters }))
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'malware_detections_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useMalwareExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters]);

  return { exporting, handleExport };
};

export const useMalwareData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: MalwareFilters = {}
): MalwareState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<MalwareState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { severityBand, malwareType, wazuhAgentId, organizationId } = filters;

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    Promise.all([
      mindtimeWazuhMalwareApi.getSummary(effectiveScope),
      mindtimeWazuhMalwareApi.getCategoriesChart(effectiveScope),
    ])
      .then(([summary, categories]) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          summary,
          categories,
          facets: mergeFacets([], prev.facets, Object.keys(categories?.categories ?? {})),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMalwareData.summary');
          setState((prev) => ({
            ...prev,
            summary: null,
            categories: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhMalwareApi
      .getDetections(
        buildMalwareQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items.items, prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMalwareData.detections');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    severityBand,
    malwareType,
    wazuhAgentId,
    organizationId,
  ]);

  return state;
};
